import './LoiComparisonTab.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStateSelector } from '../../../store/selectors';
import { fetchLoiComparison } from '../../../slices/marketplace/loi-comparison/loi-comparison-api';
import { LoiComparisonFormDto } from '../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import {
    LoiComparisonSubTab,
    LoiComparisonSubTabs,
} from '../../../slices/marketplace/loi-comparison/models/loi-comparison-sub-tab.enum';
import { LoiComparisonSummary } from './LoiComparisonSummary/LoiComparisonSummary';
import { LoiComparisonLegalIssues } from './LoiComparisonLegalIssues/LoiComparisonLegalIssues';
import createNotification from '../../../utils/createNotification';
import Helpers from '../../../utils/helper';

export interface LoiComparisonTabProps {
    engagementId: number;
    levelOfService: number;
    setSearchParams: (params: { [key: string]: string }) => void;
    isSeller: boolean;
    isBroker: boolean;
    loiComparisonEventId: string | null;
    isEventCompleted: boolean;
    fetchData: () => void;
}

export const LoiComparisonTab = ({
    engagementId,
    levelOfService,
    setSearchParams,
    isSeller,
    isBroker,
    loiComparisonEventId,
    isEventCompleted,
    fetchData,
}: LoiComparisonTabProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [items, setItems] = useState<LoiComparisonFormDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<number>(
        LoiComparisonSubTab.LoiForm
    );
    const [searchParams] = useSearchParams({});

    const loadItems = useCallback(() => {
        if (!axios) {
            return;
        }
        setIsLoading(true);
        fetchLoiComparison(axios, engagementId)
            .then((data) => {
                setItems(data);
            })
            .catch(() => {
                createNotification(
                    'Could not load LOI comparison. Please try again.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [axios, engagementId]);

    const handleAfterSelection = useCallback(() => {
        fetchData();
        loadItems();
    }, [fetchData, loadItems]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    useEffect(() => {
        const value = searchParams.get('subtab');
        const numberValue = Number(value);
        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= LoiComparisonSubTabs.length
        ) {
            setActiveTab(numberValue);
        }
    }, [searchParams]);

    const renderTabContent = () => {
        if (isLoading) {
            return Helpers.renderTableLoader();
        }
        switch (activeTab) {
            case LoiComparisonSubTab.LoiForm:
                return (
                    <LoiComparisonSummary
                        items={items}
                        levelOfService={levelOfService}
                        engagementId={engagementId}
                        isSeller={isSeller}
                        isBroker={isBroker}
                        loiComparisonEventId={loiComparisonEventId}
                        isEventCompleted={isEventCompleted}
                        fetchData={handleAfterSelection}
                        onSetItems={setItems}
                    />
                );
            case LoiComparisonSubTab.LegalIssues:
                return <LoiComparisonLegalIssues items={items} mode="legalIssues" />;
            case LoiComparisonSubTab.AdditionalNdaCovenants:
                return <LoiComparisonLegalIssues items={items} mode="covenants" />;
            default:
                return <></>;
        }
    };

    return (
        <div className="loi-comparison-tab">
            <div className="body_inner_content">
                <div className="RRT__container">
                    <div className="RRT__tabs body-tabs">
                        {LoiComparisonSubTabs.map((item) => (
                            <div
                                key={item.id}
                                className={
                                    activeTab === item.id
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab'
                                }
                                onClick={() => {
                                    setActiveTab(item.id);
                                    setSearchParams({
                                        subtab: item.id.toString(),
                                    });
                                }}>
                                {item.content}
                            </div>
                        ))}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        </div>
    );
};
